import { useState, useEffect } from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { FileDown, ChevronRight } from 'lucide-react';

export const LegalLayout = ({ title, lastUpdated, effectiveDate, sections, icon: Icon }) => {
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 120,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-background border-b border-border">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
            <div className="flex flex-col items-center text-center">
              {Icon && (
                <div className="w-16 h-16 rounded-2xl bg-lime-500/10 flex items-center justify-center text-lime-500 mb-6" data-testid="legal-icon">
                  <Icon className="w-8 h-8" />
                </div>
              )}
              <Badge className="mb-4 inline-flex items-center rounded-full border border-lime-500/30 bg-lime-500/10 px-3 py-1 text-xs font-medium text-lime-600 dark:text-lime-400">
                Legal Document
              </Badge>
              <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-4" data-testid="legal-title">
                {title}
              </h1>
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                {effectiveDate && (
                  <span>Effective: {effectiveDate}</span>
                )}
                {lastUpdated && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span>Last Updated: {lastUpdated}</span>
                  </>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-6 rounded-full gap-2"
                onClick={() => window.print()}
                data-testid="download-pdf-btn"
              >
                <FileDown className="w-4 h-4" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Sticky Sidebar */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Contents
                </h3>
                <ScrollArea className="h-[calc(100vh-200px)]">
                  <nav className="space-y-1 pr-4">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 group ${
                          activeSection === section.id
                            ? 'bg-lime-500/10 text-lime-600 dark:text-lime-400 font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                        data-testid={`nav-section-${index}`}
                      >
                        <ChevronRight className={`w-3 h-3 transition-transform ${
                          activeSection === section.id ? 'rotate-90 text-lime-500' : 'text-muted-foreground/50 group-hover:translate-x-0.5'
                        }`} />
                        <span className="truncate">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </ScrollArea>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:col-span-9">
              <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-heading prose-headings:tracking-tight prose-h2:text-2xl prose-h2:font-semibold prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-lg prose-h3:font-medium prose-p:leading-relaxed prose-li:leading-relaxed">
                {sections.map((section, index) => (
                  <section 
                    key={section.id} 
                    id={section.id} 
                    className="scroll-mt-32 mb-12 pb-8 border-b border-border last:border-0"
                    data-testid={`content-section-${index}`}
                  >
                    <h2 className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-lime-500/10 text-lime-500 text-sm font-bold">
                        {index + 1}
                      </span>
                      {section.title}
                    </h2>
                    <div className="mt-4">
                      {section.content}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalLayout;
